import { useEffect, useState } from 'react'

import { useTranslation } from 'next-i18next'

import { Breakpoint, useCheckBreakpoint } from '~/utils'
import TopicDetailStats from '~/statistics-v2/components/TopicDetailStats'
import GradeDropdown from '~/statistics-v2/components/GradeDropdown'
import ProfileHeader from '~/statistics-v2/components/ProfileHeader'
import StatsBody from '~/statistics-v2/components/StatsBody'
import { useGetUserStats } from '~/statistics-v2/hooks/useGetUserStats'
import { toGradeKey } from '~/statistics-v2/helper/statistics.helper'
import { TopicStats } from '~/statistics-v2/types/statistics.types'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { IUser } from '~/types/user-info-types'
import GamesStats from '~/statistics-v2/components/GamesStats'

export default function StatisticsV2Page({ user }: { user?: IUser }): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const [selectedGrade, setSelectedGrade] = useState({ key: '', label: '' })
  const [hideMustKnows, setHideMustKnows] = useState(false)
  const [showTopicDetails, setShowTopicDetails] = useState<TopicStats | null>(null)
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.LG })
  const { data: gradeData, isLoading: isGradeLoading } = useGetCurrentGradeQuery(
    user?.id ? { familyMemberId: user.id } : undefined,
  )

  const {
    countryCode,
    gradeList,
    headerStats,
    overAllProgressStatsCardProps,
    mustKnowsProgressCardProps,
    masteryCertificatesProgressProps,
    topicsStats,
    isLoading,
  } = useGetUserStats(selectedGrade.key, false, user?.id)

  const onGradeSelect = (gradeK: { key: string; label: string }): void => {
    setSelectedGrade({ key: gradeK.key, label: gradeK.label })
    if (Number(gradeK.key) < 6 && gradeK.key !== '0') {
      setHideMustKnows(true)
    } else {
      setHideMustKnows(false)
    }
  }

  const onTopicSelect = (topicStats: TopicStats): void => {
    setShowTopicDetails(topicStats)
  }

  const onBackClick = (): void => {
    setShowTopicDetails(null)
  }

  useEffect(() => {
    if (gradeData?.grade) {
      const gradeLabel = gradeList[String(gradeData.grade)]

      if (gradeLabel) {
        setSelectedGrade({ key: String(gradeData.grade), label: gradeLabel })
      }
    }
    if (!gradeData?.grade && !isGradeLoading) {
      setSelectedGrade({ key: '0', label: t('allGrades') })
    }
  }, [gradeData, countryCode, isGradeLoading, gradeList, t])

  return (
    <div className="relative flex justify-center items-start py-8 bg-gradient-purple-dark min-h-screen w-full max-w-screen-19/20 mx-auto">
      <div className="flex items-start justify-start max-w-screen-lg text-white">
        {showTopicDetails && !isLoading ? (
          <TopicDetailStats
            countryCode={countryCode}
            isSmallScreen={isSmallScreen}
            onBackClick={onBackClick}
            selectedGrade={selectedGrade}
            topicStats={showTopicDetails}
            userId={user?.id}
          />
        ) : (
          <div className="flex flex-col gap-6 w-full justify-center items-center">
            <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center w-full justify-center lg:justify-start max-w-screen-19/20">
              <GradeDropdown
                currentGradeKey={gradeData?.grade ? String(gradeData.grade) : undefined}
                grades={gradeList}
                isLoading={isLoading}
                onGradeSelect={onGradeSelect}
                selectedGrade={selectedGrade}
              />
            </div>
            <ProfileHeader headerStats={headerStats} isLoading={isLoading} user={user} />
            <StatsBody
              certificates={masteryCertificatesProgressProps}
              hideMustKnows={hideMustKnows}
              isLoading={isLoading}
              isSmallScreen={isSmallScreen}
              mustKnowProgressCardProps={mustKnowsProgressCardProps}
              onSelectTopic={onTopicSelect}
              overallProgressCardProps={overAllProgressStatsCardProps}
              selectedGrade={selectedGrade}
              showTopics={selectedGrade.key !== '0'}
              topicsStats={topicsStats}
            />
            {/*
             * `user` is the family member being viewed; without it the panel answers about the parent.
             *
             * The scope follows the selection: '0' is all math, so the boxes stay lifetime, and a
             * single grade shows only what was played while on it. `key` is empty until the preselect
             * effect resolves, which would otherwise send a half-built grade key.
             */}
            {selectedGrade.key !== '' && (
              <GamesStats
                familyMemberId={user?.id}
                grade={selectedGrade.key === '0' ? undefined : toGradeKey(countryCode, selectedGrade.key)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
